'use client';

import { Form } from 'antd';
import { Button, Input, Checkbox, OtpInput } from '@/components/ui';
import { useTranslations } from 'next-intl';
import { submitLoginForm } from '../app/login/loginActions';
import { useEffect, useState, useTransition } from 'react';
import { LoginFormState } from '../app/login/loginTypes';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useCountdown } from '@/lib/hooks/useCountdown';

const initialState: LoginFormState = {
  step: 'login',
  values: {
    email: '',
    password: '',
    code: '',
    remember: false,
  },
  errors: { email: false, password: false, twoFactor: false },
  authenticationResult: undefined,
};

export default function LoginForm({ returnUrl }: { returnUrl?: string }) {
  const t = useTranslations();
  const [form] = Form.useForm();
  const [pending, start] = useTransition();
  const [state, setState] = useState<LoginFormState>(initialState);
  const [hideLoginError, setHideLoginError] = useState(false);
  const pathname = usePathname();
  const [prevPathname, setPrevPathname] = useState(pathname);
  const { secondsLeft: resendCooldown, start: startResendCooldown, reset: resetResendCooldown } = useCountdown();

  // The form instance can be reused after an internal redirect, leaving stale state (e.g. step '2fa').
  // Reset it when the path changes back to the login page.
  if (pathname !== prevPathname) {
    setPrevPathname(pathname);
    setState(initialState);
    resetResendCooldown();
  }

  useEffect(() => {
    form.resetFields();
  }, [form, pathname]);

  function callBackend(values: LoginFormState['values'], action: 'submit' | 'resend' | 'switch_method', switchToMethod?: string) {
    start(() => {
      submitLoginForm(
        {
          step: state.step,
          values,
          availableMethods: state.availableMethods,
          selectedMethod: state.selectedMethod,
          maskedTargets: state.maskedTargets,
        },
        action,
        returnUrl,
        switchToMethod,
      ).then((newState) => {
        setState(newState);
        // The backend is counting down too, so this is the seconds still left over there.
        // Restart from whatever it reports; `undefined` keeps the countdown we already have,
        // for responses that did not send a new code.
        startResendCooldown(newState.retryAfterSeconds);
      });
    });
  }

  function handleOnFinish(values: LoginFormState['values']) {
    callBackend(values, 'submit');
  }

  function resend2FACode(values: LoginFormState['values']) {
    callBackend(values, 'resend');
  }

  function switchMethod(values: LoginFormState['values'], method: string) {
    const nextValues = { ...values, code: '' };
    form.setFieldValue('code', '');
    callBackend(nextValues, 'switch_method', method);
  }

  const is2fa = state.step === '2fa';
  // Only the methods the customer actually has enabled are rendered.
  const methods = state.availableMethods ?? [];
  const loginError = state.authenticationResult && !state.authenticationResult.success ? state.authenticationResult.message : null;
  const showLoginError = Boolean(loginError) && !hideLoginError;

  useEffect(() => {
    if (loginError) {
      setHideLoginError(false);
    }
  }, [loginError]);

  return (
    <div className="login watermark">
      <Form
        className={`form login-form form-stack${is2fa ? ' twofactor' : ''}`}
        form={form}
        onFinish={handleOnFinish}
        layout="vertical"
        initialValues={{ email: '', password: '', code: '', remember: false }}
      >
        <div style={is2fa ? { display: 'none' } : undefined}>
          <Form.Item className="form-item" name="email" rules={[{ required: true, message: t('USERNAME_REQUIRED') }]}>
            <Input
              className="form-input solid"
              placeholder={t('EMAIL')}
              type="text"
              autoComplete="username"
              disabled={pending}
              onFocus={() => setHideLoginError(true)}
            />
          </Form.Item>
          <Form.Item className="form-item" name="password" rules={[{ required: true, message: t('PASSWORD_REQUIRED') }]}>
            <Input
              className="form-input solid top-20"
              placeholder={t('PASSWORD')}
              type="password"
              autoComplete="current-password"
              disabled={pending}
              onFocus={() => setHideLoginError(true)}
            />
          </Form.Item>
        </div>

        {is2fa && (
          <>
            <div className="twofactor-header">
              <div className="title">{t('2FA_HEADER')}</div>
              <div className="instruction">
                {state.selectedMethod === 'TOTP'
                  ? t('LOGIN_2FA_TOTP_INSTRUCTION')
                  : state.selectedMethod && state.maskedTargets?.[state.selectedMethod]
                    ? t('LOGIN_2FA_CODE_SENT_TO', { target: state.maskedTargets[state.selectedMethod] })
                    : null}
              </div>
            </div>

            <Form.Item className="form-item" name="code" rules={[{ required: true, message: t('LOGIN_2FA_CODE_REQUIRED') }]}>
              <OtpInput length={6} disabled={pending} status={loginError ? 'error' : undefined} autoFocus />
            </Form.Item>

            {methods.length > 1 && (
              <div className="twofactor-methods bottom-30">
                <div className="label">{t('LOGIN_2FA_USE_DIFFERENT_METHOD')}</div>
                <div className="segmented" role="tablist">
                  {methods.map((method) => {
                    const active = method === state.selectedMethod;
                    return (
                      <button
                        key={method}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        className={`segmented-item${active ? ' active' : ''}`}
                        onClick={() => !active && switchMethod(form.getFieldsValue(), method)}
                        disabled={pending || active}
                      >
                        {t(`LOGIN_2FA_METHOD_${method}`)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {showLoginError && loginError && <div className="field-error">{t(loginError)}</div>}

        <div className="form-actions-stack bottom-30 top-30">
          <div className="row">
            {is2fa ? (
              <Form.Item className="checkbox" name="remember" valuePropName="checked" label={null} noStyle>
                <Checkbox>{t('REMEMBER_ME_15_DAYS')}</Checkbox>
              </Form.Item>
            ) : (
              <Link href="/login/forgot-password" className="link forgot-password-link">
                {t('FORGOT_YOUR_PASSWORD')}
              </Link>
            )}
            <Button htmlType="submit" name="action" value="login" disabled={pending} loading={pending} extraHeight>
              {t('LOG_IN')}
            </Button>
          </div>

          {is2fa && (
            <Button
              variant="outlined"
              block
              className="resend-2fa"
              onClick={() => resend2FACode(form.getFieldsValue())}
              name="action"
              value="resend"
              disabled={pending || resendCooldown > 0}
              aria-label={t('RESEND_2FA')}
            >
              {resendCooldown > 0 ? t('RESEND_2FA_IN', { seconds: resendCooldown }) : t('RESEND_2FA')}
            </Button>
          )}
        </div>

        {is2fa && <p className="form-note top-20">{t('LOGIN_CONTACT_SUPPORT')}</p>}
      </Form>
    </div>
  );
}
